import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const CardiovascularTechnologyCoursePage: React.FC = () => {
  const cardiovascularData = {
    title: "Bachelor of Science in Cardio Pulmonary Perfusion Care Technology",
    shortName: "B.Sc Perfusion Technology",
    duration: "3 Years",
    description: "A highly specialized program training students to operate heart-lung machines during cardiac surgeries and other extracorporeal procedures.",
    overview: "B.Sc Cardio Pulmonary Perfusion Care Technology prepares students to become certified perfusionists who operate heart-lung bypass machines during cardiac surgery. The program covers cardiovascular anatomy, physiology, perfusion techniques, mechanical circulatory support, and patient monitoring. Students learn to manage extracorporeal circulation, blood gas management, and hemodynamic monitoring in critical cardiac procedures.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology/Mathematics",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Medical fitness certificate required",
      "Good manual dexterity and attention to detail",
      "Ability to work under extreme pressure"
    ],
    careerProspects: {
      roles: [
        "Perfusionist",
        "Cardiac Care Technician",
        "Cardiovascular Specialist",
        "Heart Surgery Support Technologist",
        "Extracorporeal Life Support Specialist",
        "Cardiac Catheterization Technologist",
        "Perfusion Equipment Specialist",
        "Clinical Perfusion Coordinator",
        "Perfusion Research Associate",
        "Cardiac Surgery Team Member"
      ],
      industries: [
        "Cardiac Surgery Centers",
        "Cardiothoracic Hospitals",
        "Heart Institutes",
        "Medical Equipment Companies",
        "Research Institutions",
        "Government Medical Centers",
        "Private Cardiac Hospitals",
        "Perfusion Service Companies",
        "Medical Device Manufacturing",
        "International Healthcare"
      ],
      averageSalary: "₹4-10 LPA (varies by experience and specialization)",
      topRecruiters: [
        "Fortis Escorts Heart Institute",
        "Max Heart & Vascular Institute",
        "Apollo Heart Institute",
        "Narayana Hrudayalaya",
        "AIIMS Cardiothoracic Centre",
        "Medanta Heart Institute",
        "Asian Heart Institute",
        "Government Cardiac Centers",
        "International Heart Centers",
        "Perfusion Equipment Companies"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy",
          "Cardiovascular Anatomy",
          "Human Physiology",
          "Cardiovascular Physiology",
          "Biochemistry",
          "Medical Physics",
          "Mathematics",
          "Medical Terminology"
        ]
      },
      {
        year: 2,
        subjects: [
          "Perfusion Technology",
          "Extracorporeal Circulation",
          "Blood Gas Analysis",
          "Hemodynamics",
          "Cardiac Pathology",
          "Anesthesia for Cardiac Surgery",
          "Perfusion Equipment",
          "Quality Control"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Perfusion Techniques",
          "Mechanical Circulatory Support",
          "Pediatric Perfusion",
          "ECMO Technology",
          "Research Methods",
          "Clinical Training",
          "Professional Ethics",
          "Comprehensive Examination"
        ]
      }
    ],
    admissionProcess: [
      "Apply to specialized cardiac technology institutes",
      "Entrance examination (technical aptitude)",
      "Medical fitness and dexterity assessment",
      "Document verification",
      "Interview with cardiac surgery team",
      "Fee payment and admission",
      "Intensive orientation program",
      "Begin theoretical and hands-on training"
    ],
    topColleges: [
      {
        name: "Narayana Hrudayalaya Institute",
        location: "Bangalore, Karnataka",
        fees: "₹4 LPA",
        ranking: "Leading Cardiac Institute"
      },
      {
        name: "Fortis Escorts Heart Institute",
        location: "New Delhi",
        fees: "₹5 LPA",
        ranking: "Premier Heart Center"
      },
      {
        name: "Apollo Institute of Perfusion Technology",
        location: "Chennai, Tamil Nadu",
        fees: "₹4.5 LPA",
        ranking: "Industry-Linked Program"
      },
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹15,000 PA",
        ranking: "Government Institute"
      },
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu",
        fees: "₹3.5 LPA",
        ranking: "Established Medical College"
      }
    ],
    skills: [
      "Perfusion Machine Operation",
      "Cardiovascular Monitoring",
      "Blood Gas Management",
      "Technical Problem Solving",
      "Manual Dexterity",
      "Attention to Detail",
      "Stress Management",
      "Team Coordination",
      "Critical Thinking",
      "Equipment Maintenance",
      "Emergency Response",
      "Continuous Learning"
    ],
    futureScope: "Perfusion technology is advancing with miniaturized circuits, automated systems, and advanced monitoring technologies. Future opportunities include specialization in pediatric perfusion, ECMO therapy, mechanical circulatory support, and robotic-assisted perfusion. The growing field of heart transplantation and artificial heart technology is creating new career paths for perfusion specialists.",
    faqs: [
      {
        question: "What exactly does a perfusionist do during surgery?",
        answer: "A perfusionist operates the heart-lung machine that takes over the function of the heart and lungs during cardiac surgery, allowing surgeons to operate on a still heart while maintaining blood circulation and oxygenation."
      },
      {
        question: "Is this a high-stress profession?",
        answer: "Yes, perfusion work involves high responsibility and stress as patient lives depend on precise equipment operation. However, comprehensive training prepares professionals to handle these situations with confidence and skill."
      },
      {
        question: "What are the job prospects internationally?",
        answer: "Perfusionists are in high demand globally, especially in developed countries with advanced cardiac surgery programs. International opportunities exist with proper certification and language skills."
      },
      {
        question: "How long does it take to become a certified perfusionist?",
        answer: "After completing the 3-year B.Sc program, additional certification and practical experience are required. Most programs include extensive clinical rotations and certification preparation."
      },
      {
        question: "What are the salary prospects in this field?",
        answer: "Perfusion technology offers excellent salary prospects due to the specialized nature of the work and high demand for skilled professionals. Experienced perfusionists can earn significantly higher salaries."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {cardiovascularData.title} ({cardiovascularData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={cardiovascularData} />
      </div>
    </div>
  );
};

export default CardiovascularTechnologyCoursePage;